/**
 * [BOARD MIRROR] The draft-start countdown VISUAL, extracted from
 * DraftLobby's `DraftCountdown` so the standalone board can show the exact
 * same overlay without owning any audio.
 *
 * Pure presentation - it renders whatever number it is handed:
 *   • Lobby: the audio driver in `DraftCountdown` sets `display` off Adam's
 *     kickoff clip (decode latency self-corrects instead of accumulating).
 *   • Board: `display` arrives as an absolute value on the commissioner's
 *     PREDRAFT_PROGRESS broadcast. Silent by construction.
 *
 * `display === null` means "frozen at 10 while the intro line plays".
 */
import { motion, AnimatePresence } from "framer-motion";

export const PreDraftCountdownOverlay = ({ display, firstTeam, firstLabel = "First to nominate" }) => {
  const showNum = display ?? 10;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.4 } }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center"
      style={{ background: "rgba(0,0,0,0.95)" }}
      data-testid="predraft-countdown-overlay"
    >
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255,107,0,0.10) 0%, transparent 70%)"
      }} />
      <div className="relative text-center px-6">
        <motion.p
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="font-display font-black text-xs uppercase tracking-[0.4em] text-[#FF6B00] mb-4"
        >
          Draft Night
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="font-display font-bold text-lg uppercase text-[rgba(255,255,255,0.50)] mb-2"
        >
          Starting in
        </motion.p>
        <AnimatePresence mode="wait">
          <motion.div
            key={showNum}
            initial={{ scale: 1.4, opacity: 0 }}
            animate={{ scale: 1,   opacity: 1 }}
            exit={   { scale: 0.6, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="font-display font-black"
            data-testid="predraft-countdown-number"
            style={{
              fontSize: "clamp(8rem, 20vw, 14rem)",
              lineHeight: 1,
              color: showNum <= 3 ? "#FF6B00" : "#F5F5F5",
              textShadow: showNum <= 3
                ? "0 0 60px rgba(255,107,0,0.70), 0 0 120px rgba(255,107,0,0.30)"
                : "0 0 40px rgba(255,255,255,0.10)",
            }}
          >
            {showNum}
          </motion.div>
        </AnimatePresence>
        {firstTeam && (
          <motion.p
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            className="font-body text-base text-[rgba(255,255,255,0.45)] mt-6"
            data-testid="predraft-countdown-first-team"
          >
            {firstLabel}: <span className="text-[#FF6B00] font-semibold">{firstTeam}</span>
          </motion.p>
        )}
      </div>
    </motion.div>
  );
};

export default PreDraftCountdownOverlay;
